import React from "react";
import { Row } from "react-bootstrap";
import StatCard from "./StatCard";
import type { DashboardStats } from "../../types/dashboard";
import { formatCompactCount, formatCompactCurrency } from "../../utils/format";
import {
  getCardTitles,
  getComparatorLabel,
} from "./dashboardCopy";

interface StatsCardsProps {
  stats: DashboardStats | null | undefined;
}

const StatsCards: React.FC<StatsCardsProps> = ({ stats }) => {
  if (!stats) return null;

  const titles = getCardTitles(stats.periodLabel);
  const comparator = getComparatorLabel(stats.periodLabel);

  const revenueChange = Number(stats.periodVsPrevious?.revenue ?? 0);
  const ticketsChange = Number(stats.periodVsPrevious?.bookings ?? 0);
  const refundsAdminChange = Number(
    stats.periodVsPrevious?.refundsByAdmin ?? 0,
  );
  const refundsCustomerChange = Number(
    stats.periodVsPrevious?.refundsByCustomer ?? 0,
  );

  return (
    <Row className="mb-4">
      <StatCard
        title={titles.revenue}
        value={formatCompactCurrency(stats.periodRevenue)}
        subtext={comparator}
        change={revenueChange}
        changeType={
          revenueChange === 0
            ? "neutral"
            : revenueChange > 0
              ? "positive"
              : "negative"
        }
        icon="bi-currency-rupee"
        color="warning"
      />
      <StatCard
        title={titles.tickets}
        value={formatCompactCount(stats.periodBookings)}
        subtext={comparator}
        change={ticketsChange}
        changeType={
          ticketsChange === 0
            ? "neutral"
            : ticketsChange > 0
              ? "positive"
              : "negative"
        }
        icon="bi-ticket-perforated"
        color="success"
      />
      <StatCard
        title={titles.cancelledBookings}
        value={formatCompactCount(stats.periodCancelledBookings)}
        subtext={`${formatCompactCurrency(stats.periodCancelledBookingsAmount)} lost`}
        icon="bi-x-octagon"
        color="warning"
      />
      <StatCard
        title={titles.refundsByAdmin}
        value={formatCompactCount(stats.periodRefundsByAdmin)}
        subtext={`${formatCompactCurrency(stats.periodRefundsByAdminAmount)} ${comparator}`}
        change={refundsAdminChange}
        changeType={
          refundsAdminChange === 0
            ? "neutral"
            : refundsAdminChange > 0
              ? "negative"
              : "positive"
        }
        icon="bi-shield-check"
        color="info"
      />
      <StatCard
        title={titles.refundsByCustomer}
        value={formatCompactCount(stats.periodRefundsByCustomer)}
        subtext={`${formatCompactCurrency(stats.periodRefundsByCustomerAmount)} ${comparator}`}
        change={refundsCustomerChange}
        changeType={
          refundsCustomerChange === 0
            ? "neutral"
            : refundsCustomerChange > 0
              ? "negative"
              : "positive"
        }
        icon="bi-arrow-counterclockwise"
        color="primary"
      />
      <StatCard
        title={titles.failedPayments}
        value={formatCompactCount(stats.periodFailedPayments)}
        subtext={`${formatCompactCurrency(stats.periodFailedPaymentsAmount)} lost`}
        icon="bi-exclamation-triangle"
        color="danger"
      />
    </Row>
  );
};

export default StatsCards;